<template>
  <section id="business" class="business-section">
    <div class="business-container">
      <div class="section-header">
        <div class="section-eyebrow">Full-cycle CNC equipment service</div>
        <h2 class="section-title">核心业务解决方案</h2>
        <p class="section-desc">
          围绕设备故障恢复、稳定运行、状态诊断与工程升级四类需求，提供从现场判断、方案实施到验证交付的完整服务闭环。
        </p>
      </div>

      <div class="solution-tabs" role="tablist" aria-label="核心业务分类">
        <button
          v-for="(group, index) in solutionGroups"
          :key="group.key"
          type="button"
          class="solution-tab"
          :class="{ active: activeGroup === index }"
          @click="activeGroup = index"
        >
          <span class="tab-index">0{{ index + 1 }}</span>
          <span class="tab-name">{{ group.name }}</span>
          <span class="tab-summary">{{ group.summary }}</span>
        </button>
      </div>

      <div class="solution-panel">
        <div class="panel-copy">
          <div class="panel-label">{{ currentGroup.label }}</div>
          <h3>{{ currentGroup.name }}</h3>
          <p class="panel-intro">{{ currentGroup.intro }}</p>

          <div class="panel-block">
            <div class="block-title">适用场景</div>
            <ul>
              <li v-for="(scene, idx) in currentGroup.scenes" :key="idx">{{ scene }}</li>
            </ul>
          </div>

          <div class="panel-block">
            <div class="block-title">服务重点</div>
            <ul>
              <li v-for="(point, idx) in currentGroup.points" :key="idx">{{ point }}</li>
            </ul>
          </div>

          <div class="deliverables">
            <span v-for="(item, idx) in currentGroup.deliverables" :key="idx">{{ item }}</span>
          </div>
        </div>

        <div class="panel-media" :class="`layout-${currentGroup.images.length}`">
          <figure
            v-for="(img, idx) in currentGroup.images"
            :key="img.src"
            class="media-card"
            :class="{ featured: idx === 0 }"
          >
            <ZoomableImage
              :src="getImage(img)"
              :alt="img.caption"
              :fit="img.fit || 'cover'"
              :invert="Boolean(img.invert)"
            />
            <figcaption>{{ img.caption }}</figcaption>
          </figure>
        </div>
      </div>

      <div class="service-details">
        <div class="details-title">该类服务包含</div>
        <div class="details-grid">
          <article class="service-card" v-for="service in currentGroup.services" :key="service.title">
            <div class="service-type">{{ service.type }}</div>
            <h4>{{ service.title }}</h4>
            <p>{{ service.desc }}</p>
            <div class="service-meta">
              <div>
                <strong>实施内容</strong>
                <ul>
                  <li v-for="(item, idx) in service.scope" :key="idx">{{ item }}</li>
                </ul>
              </div>
              <div>
                <strong>交付结果</strong>
                <ul>
                  <li v-for="(item, idx) in service.result" :key="idx">{{ item }}</li>
                </ul>
              </div>
            </div>
          </article>
        </div>
      </div>

      <div class="workflow">
        <div class="workflow-title">标准服务闭环</div>
        <div class="workflow-steps">
          <div class="workflow-step" v-for="(step, idx) in workflow" :key="idx">
            <span>{{ idx + 1 }}</span>
            <p>{{ step }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ZoomableImage from './ZoomableImage.vue';

export default {
  name: 'BusinessSection',
  components: {
    ZoomableImage
  },
  data() {
    return {
      activeGroup: 0,
      workflow: ['现场诊断', '方案确认', '实施作业', '功能/精度验证', '记录归档与后续建议'],
      solutionGroups: [
        {
          key: 'repair',
          label: 'Troubleshooting & Recovery',
          name: '设备维修与故障恢复',
          summary: '快速复机 + 复杂故障专项处理',
          intro: '面向停机故障、异常报警、关键部件失效和精度异常等场景，先恢复生产，再结合故障原因给出后续维护建议。',
          scenes: ['设备突发故障导致非计划停机', '主轴、传动、刀库、PLC、工作台等关键部位异常', '维修后需要功能验证或精度复核'],
          points: ['快速定位故障点，减少无效拆装', '复杂故障按专项维修组织人员和备件', '维修过程记录，形成后续预防依据'],
          deliverables: ['维修记录', '故障原因说明', '复机验证', '后续维护建议'],
          images: [
            { base: 'gallery', src: 'onsite-control.jpg', caption: '数控系统现场调试', fit: 'cover' },
            { base: 'gallery', src: 'heavy-maintenance.jpg', caption: '关键部件拆装维护', fit: 'cover' },
            { base: 'gallery', src: 'precision-parts.jpg', caption: '导轨丝杠部件检查', fit: 'cover' }
          ],
          services: [
            {
              type: '日常维修',
              title: '设备针对性维修服务',
              desc: '针对某一特定故障提供快速恢复服务，目标是缩短非计划停机时间。',
              scope: ['故障诊断与复现', '关键部位检查与参数校正', '备件更换与试运行验证'],
              result: ['设备恢复生产', '维修过程记录', '故障原因与风险点提示']
            },
            {
              type: '专项维修',
              title: '设备专项维修服务',
              desc: '处理日常维修难以解决的复杂故障，常涉及关键零部件、精度恢复或系统更换。',
              scope: ['主轴及传动系统拆装与恢复', '刀具自动交换系统维修或更换', '数控系统/PLC/工作台/液压润滑等专项处理'],
              result: ['专项维修记录', '调试测试结果', '精度恢复或进一步修复建议']
            }
          ]
        },
        {
          key: 'maintenance',
          label: 'Maintenance & Guarantee',
          name: '设备维保与年度保障',
          summary: '预防维护 + 托管保障',
          intro: '通过深度保养、设备档案和年度托管，把“故障后维修”前移为“故障前预防”，降低设备管理成本和生产风险。',
          scenes: ['设备长期高负荷运行，需要阶段性深度保养', '客户希望建立设备档案和维保计划', '需要外包托管设备保障工作'],
          points: ['对机械、电气、液压、润滑、冷却等系统进行系统性检查', '建立设备保养、维修、数据备份和备件记录', '按年度保障模式稳定设备生命周期表现'],
          deliverables: ['保养清单', '设备档案', '年度计划', '阶段性总结'],
          images: [
            { base: 'business', src: 'image12_white.png', caption: '深度保养流程框架', fit: 'contain' },
            { base: 'business', src: 'image14.jpeg', caption: '主轴油缸保养对比', fit: 'cover' },
            { base: 'business', src: 'image20.jpeg', caption: '年度保障模式说明', fit: 'contain' }
          ],
          services: [
            {
              type: '二级维护',
              title: '设备集中深度保养服务',
              desc: '对设备进行单次深层保养，发现故障隐患，实现事前预防。',
              scope: ['各组成部分检查、清洁与保养', '功能状态与精度状态检测', '油品添加更换与机床数据备份'],
              result: ['保养检查清单', '检测记录', '设备档案资料']
            },
            {
              type: '托管保障',
              title: '设备年度保障服务',
              desc: '涵盖日常维修、设备保养、档案建立和数据管理的一体式外包托管服务。',
              scope: ['年度维保计划', '故障与保养记录管理', '备件与技术支持建议'],
              result: ['年度保障记录', '设备运行档案', '设备管理优化建议']
            }
          ]
        },
        {
          key: 'diagnosis',
          label: 'Condition Monitoring',
          name: '状态检测与精度诊断',
          summary: '功能检测 + 精度数据支撑',
          intro: '利用标准化检查和专业检测设备获取设备当前状态，为维修方向、精度恢复和持续稳定生产提供数据依据。',
          scenes: ['加工精度波动但故障点不明确', '需要对设备状态做阶段性评估', '维修、搬迁或改造前后需要数据验证'],
          points: ['标准功能状态检测：机械、电气、液压、润滑、冷却等系统', '特殊功能检测：主轴振动、换刀位置校正等', '精度状态检测：几何精度、定位精度、补偿与激光检测'],
          deliverables: ['状态检测报告', '问题清单', '精度数据', '修复方向建议'],
          images: [
            { base: 'business', src: 'image21.png', caption: '检测装备与项目说明', fit: 'contain' },
            { base: 'business', src: 'image22.png', caption: '设备状态检测', fit: 'contain' },
            { base: 'business', src: 'image23.png', caption: '精度与功能验证', fit: 'contain' }
          ],
          services: [
            {
              type: '三级维护',
              title: '设备状态检测服务',
              desc: '分为标准功能状态检测、特殊功能状态检测和精度状态检测。',
              scope: ['“望闻问切”式标准化巡检', '主轴振动分析与换刀位置校正', '几何精度、定位精度及补偿检测'],
              result: ['检测记录', '关键状态数据', '维修与精度恢复建议']
            }
          ]
        },
        {
          key: 'engineering',
          label: 'Engineering Service',
          name: '搬迁改造与工程服务',
          summary: '搬迁就位 + 系统升级 + 定制提升',
          intro: '围绕设备位置变化、系统升级、加工能力提升和现场环境优化，提供从方案评估到实施交付的工程化服务。',
          scenes: ['设备跨区域搬迁、复装和重新就位', '老旧设备需要恢复功能或提升自动化能力', '需要加装功能或优化环境以保障长期稳定运行'],
          points: ['搬迁前勘查、拆解防护、运输、就位和调试', '机械、电气、数控系统改造与功能恢复', '按客户工艺要求进行定制化加装和环境优化'],
          deliverables: ['工程方案', '实施记录', '调试验收', '使用建议'],
          images: [
            { base: 'business', src: 'image24.png', caption: '搬迁流程规划', fit: 'contain' },
            { base: 'business', src: 'image25.png', caption: '搬迁实施环节', fit: 'cover' },
            { base: 'business', src: 'image26.png', caption: '搬迁就位与调试', fit: 'cover' }
          ],
          services: [
            {
              type: '搬迁服务',
              title: '设备搬迁一条龙服务',
              desc: '提供设备从原有位置到目标位置的拆解、搬运、就位和调试服务。',
              scope: ['搬迁前勘查与方案确认', '拆解、标识、包装防护和运输', '安装、找平找正、接线接管与调试'],
              result: ['搬迁方案', '复装调试记录', '移交确认']
            },
            {
              type: '升级改造',
              title: '设备改造服务',
              desc: '通过结构、机械、电气或数控系统改造，恢复设备功能并提升精度、效率和自动化程度。',
              scope: ['故障部位改造与功能恢复', '普通设备数控化改造', '数控系统升级与自动化功能恢复'],
              result: ['改造方案', '调试数据', '功能验收记录']
            }
          ]
        }
      ]
    };
  },
  computed: {
    currentGroup() {
      return this.solutionGroups[this.activeGroup];
    }
  },
  methods: {
    getImage(img) {
      return require('@/assets/images/' + img.base + '/' + img.src);
    }
  }
};
</script>

<style scoped>
.business-section {
  background:
    radial-gradient(circle at top left, rgba(25, 118, 210, 0.08), transparent 34%),
    linear-gradient(180deg, #f6f9fd 0%, #ffffff 100%);
  padding: 88px 0;
}

.business-container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-header {
  max-width: 820px;
  margin-bottom: 34px;
}

.section-eyebrow {
  color: #1976d2;
  font-size: 0.88rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.section-title {
  font-size: 2.2rem;
  font-weight: 900;
  color: #132033;
  margin: 0 0 12px 0;
  letter-spacing: 1px;
}

.section-desc {
  margin: 0;
  color: #536173;
  line-height: 1.9;
  font-size: 1rem;
}

.title-underline {
  width: 64px;
  height: 3px;
  background: #1976d2;
  border-radius: 999px;
  margin: 14px 0 0;
}

.solution-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

.solution-tab {
  text-align: left;
  border: 1px solid #e5edf6;
  background: rgba(255, 255, 255, 0.86);
  border-radius: 18px;
  padding: 18px;
  cursor: pointer;
  transition: all 0.22s ease;
  min-height: 148px;
}

.solution-tab:hover,
.solution-tab.active {
  transform: translateY(-2px);
  border-color: rgba(25, 118, 210, 0.38);
  box-shadow: 0 18px 42px rgba(25, 118, 210, 0.11);
  background: #ffffff;
}

.tab-index {
  display: block;
  color: #1976d2;
  font-weight: 900;
  margin-bottom: 14px;
}

.tab-name {
  display: block;
  color: #132033;
  font-size: 1.08rem;
  font-weight: 900;
  margin-bottom: 8px;
}

.tab-summary {
  color: #6a7584;
  line-height: 1.6;
  font-size: 0.92rem;
}

.solution-panel {
  display: grid;
  grid-template-columns: 1fr 0.92fr;
  gap: 22px;
  background: #ffffff;
  border: 1px solid #e8eef6;
  border-radius: 26px;
  padding: 24px;
  box-shadow: 0 22px 58px rgba(16, 35, 64, 0.08);
}

.panel-copy {
  padding: 12px 6px 8px 4px;
}

.panel-label {
  color: #1976d2;
  font-size: 0.8rem;
  font-weight: 900;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.panel-copy h3 {
  margin: 0 0 12px;
  color: #132033;
  font-size: 1.75rem;
  line-height: 1.3;
}

.panel-intro {
  margin: 0 0 18px;
  color: #536173;
  line-height: 1.9;
}

.panel-block {
  background: #f7faff;
  border: 1px solid #e8eef6;
  border-radius: 16px;
  padding: 14px 16px;
  margin-top: 12px;
}

.block-title {
  color: #132033;
  font-weight: 900;
  margin-bottom: 8px;
}

.panel-block ul,
.service-meta ul {
  margin: 0;
  padding-left: 18px;
  color: #536173;
  line-height: 1.8;
}

.deliverables {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

.deliverables span {
  color: #1976d2;
  background: rgba(25, 118, 210, 0.09);
  border: 1px solid rgba(25, 118, 210, 0.13);
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 0.9rem;
  font-weight: 800;
}

.panel-media {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: 210px;
  gap: 12px;
}

.media-card {
  position: relative;
  margin: 0;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e8eef6;
  background: #f8fafc;
}

.media-card.featured {
  grid-row: span 2;
}

.media-card img {
  width: 100%;
  height: 100%;
  display: block;
}

.media-card figcaption {
  position: absolute;
  left: 10px;
  bottom: 10px;
  color: #ffffff;
  background: rgba(19, 32, 51, 0.72);
  backdrop-filter: blur(8px);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.82rem;
}

.details-title {
  margin: 28px 0 14px;
  color: #132033;
  font-size: 1.2rem;
  font-weight: 900;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.service-card {
  background: #ffffff;
  border: 1px solid #e8eef6;
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 12px 30px rgba(16, 35, 64, 0.05);
}

.service-type {
  display: inline-flex;
  color: #1976d2;
  background: rgba(25, 118, 210, 0.09);
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.82rem;
  font-weight: 900;
  margin-bottom: 12px;
}

.service-card h4 {
  margin: 0 0 10px;
  color: #132033;
  font-size: 1.18rem;
}

.service-card p {
  margin: 0 0 16px;
  color: #536173;
  line-height: 1.8;
}

.service-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.service-meta strong {
  display: block;
  color: #132033;
  margin-bottom: 8px;
}

.workflow {
  margin-top: 22px;
  background: #132033;
  color: #ffffff;
  border-radius: 22px;
  padding: 20px;
}

.workflow-title {
  font-size: 1.05rem;
  font-weight: 900;
  margin-bottom: 14px;
}

.workflow-steps {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.workflow-step {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 15px;
  padding: 12px;
}

.workflow-step span {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #1976d2;
  font-weight: 900;
  margin-bottom: 8px;
}

.workflow-step p {
  margin: 0;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
}

@media (max-width: 1100px) {
  .solution-tabs {
    grid-template-columns: repeat(2, 1fr);
  }

  .solution-panel {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .business-section {
    padding: 60px 0;
  }

  .solution-tabs,
  .details-grid,
  .service-meta,
  .workflow-steps {
    grid-template-columns: 1fr;
  }

  .solution-panel {
    padding: 18px;
  }

  .panel-media {
    grid-template-columns: 1fr;
    grid-auto-rows: 220px;
  }

  .media-card.featured {
    grid-row: span 1;
  }

  .section-title {
    font-size: 1.75rem;
  }
}
</style>
